import { useMemo } from "react";
import Card, { CardHeader } from "components/card";
import Chart from "components/charts/Chart";
import { ARCHETYPE_CHART_COLORS, ARCHETYPE_KEYS, ARCHETYPES } from "lib/archetypes";
import { formatDate } from "utils/format";

const BASE_OPTIONS = {
  chart: { stacked: true },
  colors: ARCHETYPE_CHART_COLORS,
  fill: { type: "gradient", gradient: { opacityFrom: 0.5, opacityTo: 0.1 } },
  stroke: { curve: "smooth" },
  legend: { position: "bottom", horizontalAlign: "center" },
};

/**
 * The API returns one row per (month, primaryStyle) with `month` as a UTC
 * DATE_TRUNC timestamp. Rows are folded into one x category per month.
 */
const buildTimeline = (timeline) => {
  const months = [];
  const indexByMonth = new Map();
  const counts = Object.fromEntries(ARCHETYPE_KEYS.map((key) => [key, []]));

  timeline.forEach(({ month, primaryStyle, count }) => {
    if (!indexByMonth.has(month)) {
      indexByMonth.set(month, months.length);
      months.push(month);
      ARCHETYPE_KEYS.forEach((key) => counts[key].push(0));
    }
    if (counts[primaryStyle]) {
      counts[primaryStyle][indexByMonth.get(month)] += Number(count) || 0;
    }
  });

  return {
    categories: months.map((month) => formatDate(month, "UTC")),
    series: ARCHETYPE_KEYS.map((key) => ({ name: ARCHETYPES[key].label, data: counts[key] })),
  };
};

const AssessmentTimeline = ({ timeline }) => {
  const { series, options } = useMemo(() => {
    const built = buildTimeline(timeline || []);
    return {
      series: built.series,
      options: { ...BASE_OPTIONS, xaxis: { categories: built.categories } },
    };
  }, [timeline]);

  if (!timeline) return null;

  return (
    <Card padding="md">
      <CardHeader
        title="Assessment Timeline"
        description="Completed assessments per month, last 6 months"
        className="mb-4"
      />
      <Chart
        type="area"
        series={series}
        options={options}
        height="lg"
        emptyText="No assessments in the last 6 months"
      />
    </Card>
  );
};

export default AssessmentTimeline;
